import { useCallback, useEffect } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTableStateParams } from '@/hooks/useTableStateParams';
import { PageHeader } from '@/components/ui/page-header';
import { useMaterialRequestStore } from '../store';
import { MaterialRequestTable } from '../components';

/** Maps frontend TanStack column IDs to backend sortBy param names */
function toBackendSortBy(columnId: string | undefined): string | undefined {
  if (!columnId) return undefined;
  if (columnId === 'requestorFullName') return 'requestorName';
  return columnId;
}

export function MaterialRequestsPage() {
  usePageTitle('Material Requests');

  const {
    requests,
    totalCount,
    totalPages,
    isLoadingPaged,
    pagedError,
    syncError,
    fetchRequestsPaged,
  } = useMaterialRequestStore();

  const { sorting, pagination, onSortingChange, onPaginationChange } =
    useTableStateParams({ defaultPageSize: 10 });

  const fetchPage = useCallback(() => {
    fetchRequestsPaged({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: toBackendSortBy(sorting[0]?.id),
      sortDesc: sorting[0]?.desc ?? true,
    });
  }, [fetchRequestsPaged, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Material Requests"
        description={
          <>
            View approved Material Request Slips (MRS) synced from NetSuite.
            {syncError && (
              <span className="text-destructive ml-2">Sync failed: {syncError}</span>
            )}
          </>
        }
      />
      {pagedError ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{pagedError}</p>
          <button
            onClick={fetchPage}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        <MaterialRequestTable
          data={requests}
          isLoading={isLoadingPaged}
          totalCount={totalCount}
          pageCount={totalPages}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          onRefresh={fetchPage}
        />
      )}
    </div>
  );
}
